import { avSelectors, useAvContext } from "@minervaproject/avsubsystem";
import { useCallback } from "react";

import { useAvDeviceOptions } from "components/av/avsubsystem-utils";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { Menu, MenuItem, SubMenu } from "mds/components/Menu";
import { ToggleSwitch } from "mds/components/ToggleSwitch";
import { DotMenuVerticalIcon, MicrophoneIcon, RefreshIcon, VideoIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectIsBackgroundBlurEnabled } from "store/selectors";
import { localStateActions } from "store/slices/local";

export const AudioVideoOptionsMenuItems = () => {
  const avConnection = useAvContext();
  const dispatch = useAppDispatch();
  const selectedDevices = avSelectors.useSelectedDevices();
  const isBackgroundBlurEnabled = useAppSelector(selectIsBackgroundBlurEnabled);

  const refreshAv = useCallback(() => {
    avConnection?.refreshAv();
  }, [avConnection]);

  const { cameraOptions, micOptions } = useAvDeviceOptions();
  const onToggleBackgroundBlur = useCallback(() => {
    dispatch(localStateActions.setIsBackgroundBlurEnabled(!isBackgroundBlurEnabled));
  }, [dispatch, isBackgroundBlurEnabled]);

  const onChangeCamera = useCallback(
    (device) => {
      if (!avConnection) return;
      avConnection.selectCamera(device.id);
      // the title is the label of the device
      dispatch(localStateActions.setPreferredCameraDeviceLabel(device.title));
    },
    [avConnection, dispatch],
  );

  const onChangeMic = useCallback(
    (device) => {
      if (!avConnection) return;
      avConnection.selectMic(device.id);
      dispatch(localStateActions.setPreferredMicDeviceLabel(device.title));
    },
    [avConnection, dispatch],
  );

  return (
    <>
      <SubMenu
        label={<IconText iconStart={<MicrophoneIcon />} text={t("av.audio_source")} />}
        withArrow
      >
        {micOptions.length === 0 ? (
          <MenuItem disabled>No microphones</MenuItem>
        ) : (
          micOptions.map((option) => (
            <MenuItem
              className={option.id === selectedDevices?.selectedMic?.id ? "selected" : undefined}
              eventName="AV Settings - Audio source updated"
              key={option.id}
              onClick={() => onChangeMic(option)}
            >
              {option.title}
            </MenuItem>
          ))
        )}
      </SubMenu>
      <SubMenu label={<IconText iconStart={<VideoIcon />} text={t("av.video_source")} />} withArrow>
        {cameraOptions.length === 0 ? (
          <MenuItem disabled>No cameras</MenuItem>
        ) : (
          cameraOptions.map((option) => (
            <MenuItem
              className={option.id === selectedDevices?.selectedCam?.id ? "selected" : undefined}
              eventName="AV Settings - Video source updated"
              key={option.id}
              onClick={() => onChangeCamera(option)}
            >
              {option.title}
            </MenuItem>
          ))
        )}
      </SubMenu>
      <MenuItem eventName="AV Settings - Refresh AV" onClick={refreshAv}>
        <IconText iconStart={<RefreshIcon />} text={t("av.refresh_av")} />
      </MenuItem>
      <MenuItem eventName="AV Settings - Background blur" onClick={onToggleBackgroundBlur}>
        <div className="flex w-full items-center justify-between">
          <span className="mr-2">{t("av.background_blur")}</span>
          <ToggleSwitch
            eventName="Toggle Background Blur"
            isSelected={isBackgroundBlurEnabled}
            onClick={onToggleBackgroundBlur}
          />
        </div>
      </MenuItem>
    </>
  );
};

const AudioVideoOptionsMenu = () => {
  const MenuButton = (
    <Button
      className="w-[24px] min-w-[24px]"
      color="transparentSquareLegacy"
      eventName="AV Settings - Open menu"
      size="l"
      title={t("av.av_settings")}
      isSquare
    >
      <DotMenuVerticalIcon color="white" />
    </Button>
  );

  return (
    <Menu className="dark-menu min-w-[180px]" direction="top" menuButton={MenuButton}>
      <AudioVideoOptionsMenuItems />
    </Menu>
  );
};

// Using default export to allow lazy-loading
// eslint-disable-next-line import/no-default-export
export default AudioVideoOptionsMenu;
